import { ImageBrokenIcon } from '@phosphor-icons/react';
import { useEffect, useMemo, type JSX } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { useIssueAttachmentBlobQuery } from '@/hooks/useIssueReportQueries';

/**
 * Renders an authorized issue-attachment image. The attachment endpoint is Super-Admin-gated and
 * requires the bearer token, so a plain <img src> won't work — we fetch the blob through axios (which
 * attaches the token) and render an object URL, revoking it on change/unmount.
 */
export interface AttachmentThumbProps {
  readonly reportId: number;
  readonly attachmentId: number;
  readonly alt: string;
  readonly className?: string;
}

export function AttachmentThumb({
  reportId,
  attachmentId,
  alt,
  className,
}: AttachmentThumbProps): JSX.Element {
  const { data: blob, isLoading, isError } = useIssueAttachmentBlobQuery(reportId, attachmentId);

  const objectUrl = useMemo(() => (blob !== undefined ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const base = className ?? 'h-12 w-12 rounded border border-input object-cover';

  if (isLoading) {
    return <Skeleton className={base} data-testid="attachment-thumb-loading" />;
  }
  if (isError || objectUrl === null) {
    return (
      <span
        className={`${base} flex items-center justify-center bg-muted text-muted-foreground`}
        data-testid="attachment-thumb-error"
        title="Could not load image"
      >
        <ImageBrokenIcon size={16} />
      </span>
    );
  }
  return <img src={objectUrl} alt={alt} className={base} data-testid="attachment-thumb" />;
}
